import { useEffect, useState } from "react";
import useAppDispatch from "@/hooks/useAppDispatch";
import { handleApiError } from "@/helpers/apiErrorHelper";
import wooRestApiService from "@/api/services/wooRestApiService";
import { shippingToPaymentMap } from "@/constants/mapping";
import type { ShippingMethodId } from "@/constants/enums";
import type { PaymentGateway } from "@/types/payments";

//#region Types
export interface UsePaymentMethodsReturn {
  paymentMethods: PaymentGateway[] | null;
  isPaymentMethodsLoading: boolean;
  getAllowedPaymentMethods: (shippingMethodIdField: ShippingMethodId) => PaymentGateway[];
}
//#endregion

const usePaymentMethods = () => {
  const appDispatch = useAppDispatch();

  const [paymentMethods, setPaymentMethods] = useState<PaymentGateway[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getAllowedPaymentMethods = (shippingMethodIdField: ShippingMethodId) =>
    paymentMethods?.filter((method) =>
      (shippingToPaymentMap[shippingMethodIdField] || []).includes(method.id),
    ) ?? [];

  const fetchPaymentMethods = async (signal?: AbortSignal) => {
    setIsLoading(true);

    try {
      const { data } = await wooRestApiService.paymentGateways.getAll(signal);
      const enabledMethods = data.filter((method) => method.enabled);
      setPaymentMethods(enabledMethods);
    } catch (error) {
      handleApiError(error, appDispatch, "取得付款方式失敗，請稍後再試。");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchPaymentMethods(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  return {
    paymentMethods,
    isPaymentMethodsLoading: isLoading,
    getAllowedPaymentMethods,
  };
};

export default usePaymentMethods;
